/**
 * `<CascadePromptModal>` — cascade decision prompt.
 *
 * Fires ONLY when N > 0 (candidate has other in-flight JD pairings).
 * When N === 0, the parent skips this modal entirely (global-patterns §4.4
 * + Invariant CASC-MOD-1).
 *
 * Buttons (per §4.4):
 *   Left (default focus, autoFocus): "Keep active in other jobs" — secondary variant.
 *   Right: "Withdraw from {N} other jobs" — secondary variant.
 *   Both secondary — neither is a celebratory moment; both are valid choices.
 *
 * Keyboard: Enter activates focused button (the default-focused left one).
 *           Esc closes as cancel (no cascade mode selected = keep_active default).
 *
 * Invariants: CASC-DEF-1 (default keep_active), CASC-MOD-3 (both secondary),
 *             CASC-MOD-4 (default focus on "Keep active").
 *
 * Spec: global-patterns-spec.md §4.4 / candidate-detail-rebuild-spec.md §7.
 */
import { type JSX } from "react";
import { Button } from "@/components/common/Button";
import type { TerminalTenantStatus } from "@/lib/candidatesApi";

const STATUS_LABELS: Record<TerminalTenantStatus, string> = {
  hired: "Hired",
  rejected: "Rejected",
  opted_out: "Opted Out",
};

interface CascadePromptModalProps {
  candidateName: string;
  jdTitle: string;
  newStatus: TerminalTenantStatus;
  /** Count of OTHER in-flight JD pairings (not the current JD). Must be > 0. */
  otherInFlightCount: number;
  isPending: boolean;
  onKeepActive: () => void;
  onApplyToAll: () => void;
  onCancel: () => void;
}

export function CascadePromptModal({
  candidateName,
  jdTitle,
  newStatus,
  otherInFlightCount,
  isPending,
  onKeepActive,
  onApplyToAll,
  onCancel,
}: CascadePromptModalProps): JSX.Element {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  const statusLabel = STATUS_LABELS[newStatus];
  const withdrawLabel =
    otherInFlightCount === 1
      ? "Withdraw from 1 other job"
      : `Withdraw from ${otherInFlightCount} other jobs`;

  return (
    <div
      className="pointer-events-auto fixed inset-0 z-50 flex items-center justify-center bg-surface-overlay backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cascade-modal-title"
      aria-describedby="cascade-modal-desc"
      onKeyDown={handleKeyDown}
      onClick={onCancel}
      data-testid="cascade-prompt-modal"
    >
      <div
        className="w-full max-w-md rounded-xl border border-border-default bg-surface-elevated p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
        data-testid="cascade-modal-panel"
      >
        <h2
          id="cascade-modal-title"
          className="mb-2 text-lg font-semibold text-text-emphasis"
        >
          {candidateName} is now {statusLabel} in {jdTitle}.
        </h2>
        <p
          id="cascade-modal-desc"
          className="mb-1.5 text-sm text-text-secondary"
        >
          {candidateName.split(" ")[0]} has {otherInFlightCount} other in-flight
          application{otherInFlightCount === 1 ? "" : "s"}. Apply this status
          across all of them?
        </p>
        <p className="mb-6 text-xs text-text-tertiary">
          You can withdraw individual applications later.
        </p>

        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          {/* autoFocus on the safe default action — CASC-MOD-4.
              Button does not forward refs; autoFocus on the native button
              element is set via the spread props. */}
          <Button
            variant="secondary"
            disabled={isPending}
            onClick={onKeepActive}
            data-testid="cascade-keep-active"
            autoFocus
          >
            Keep active in other jobs
          </Button>
          <Button
            variant="secondary"
            disabled={isPending}
            onClick={onApplyToAll}
            data-testid="cascade-apply-all"
          >
            {withdrawLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
